import { useCanManageEstate } from '../../services/access-policy';

import React, { useState, useEffect } from 'react';
import { 
  List, 
  Tag, 
  Typography, 
  Space, 
  Button, 
  Divider, 
  Tooltip, 
  Menu, 
  message, 
  Dropdown, 
  Modal, 
  Upload, 
  Input, 
  Alert, 
  Spin 
} from 'antd';
import { 
  EllipsisOutlined, 
  UploadOutlined, 
  DownloadOutlined, 
  EyeOutlined, 
  CloseOutlined, 
  TagOutlined, 
  PlusOutlined,
  PartitionOutlined,
  DeleteOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import api from '../../services/api';
import AddProbateDocumentModal from './AddProbateDocumentModal';
import PdfPreviewModal from './PdfPreviewModal';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../store/GlobalContext';

const { Text, Title } = Typography;

// Styled Components
const DocumentContainer = styled.div`
  max-width: 1000px;
  margin: auto;
  padding: 2rem;
  background: #ffffff;
  position: relative; /* To position the floating button */
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-radius: 8px;
  background-color: #f4f6fb;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const DocumentDetails = styled.div`
  flex: 1;
`;

const DocumentTitle = styled(Title)`
  font-size: 1.2rem;
  margin: 0;
  font-weight: bold;
`;

const DocumentDescription = styled(Text)`
  display: block;
  margin: 0.5rem 0;
  color: #888888;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Number of lines to show */
  -webkit-box-orient: vertical;
`;

const DocumentTags = styled(Space)`
  margin-top: 0.5rem;
`;

const DocumentStatus = styled(Tag)<{ status: string }>`
  color: ${({ status }) => (status === 'Signed' ? '#52c41a' : '#faad14')};
  background-color: ${({ status }) => (status === 'Signed' ? '#f6ffed' : '#fffbe6')};
`;

const DocumentActions = styled(Space)`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #888888;
`;

const AddTagInput = styled(Input)`
  width: 200px;
`;

const FloatingButton = styled(Button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1000;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Types
interface Tag {
  id: number;
  name: string;
}

interface ProbateDocument {
  id: number;
  uid: string;
  title: string;
  description: string;
  tags: Tag[];
  status: string;
}

const DocumentsPage: React.FC = () => {
  // State for Documents
  const [documents, setDocuments] = useState<ProbateDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(true);
  const [errorDocs, setErrorDocs] = useState<string | null>(null);

  // Global Context
  const { selectedEstate } = useGlobalContext();
  const canManage = useCanManageEstate();

  // State for Upload Modal
  const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
  const [currentDocId, setCurrentDocId] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // State for Add Tag Modal
  const [addTagModalVisible, setAddTagModalVisible] = useState<boolean>(false);
  const [tagName, setTagName] = useState<string>('');
  const [selectedDocId, setSelectedDocId] = useState<string>('');

  // State for Add Probate Document Modal
  const [isAddModalVisible, setIsAddModalVisible] = useState<boolean>(false);

  // State for PDF Preview Modal
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [previewDocId, setPreviewDocId] = useState<string>('');
  const [previewDocTitle, setPreviewDocTitle] = useState<string>('');

  const handleOpenPreview = (docId: string) => {
    const doc = documents.find((d: any) => d.uid === docId);
    setPreviewDocId(docId);
    setPreviewDocTitle(doc?.title || 'Document');
    setPreviewVisible(true);
  };

  // Navigation hook
  const navigate = useNavigate();

  // Fetch documents from backend
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await api.get('/probatedocs');
        setDocuments(response.data);
      } catch (error: any) {
        console.error(error);
        setErrorDocs(error.response?.data?.message || 'Failed to fetch documents.');
      } finally {
        setLoadingDocs(false);
      }
    };

    fetchDocuments();
  }, []);

  /** The document currently being fetched, so its menu shows why it is waiting. */
  const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);

  /**
   * Handles menu item clicks based on the selected action.
   * @param e - The menu click event.
   * @param docId - The UID of the selected probate document.
   */
  const handleMenuClick = async (e: any, docId: string) => {
    switch (e.key) {
      case '1': // Download Original
        await handleDownloadOriginal(docId);
        break;
      case 'preview':
        handleOpenPreview(docId);
        break;
      case '7': // Download Filled
        await handleDownloadFilled(docId);
        break;
      case '2': // Download User Updated
        await handleDownloadUserVersion(docId);
        break;
      case '3': // Upload User Updated Document
        openUploadModal(docId);
        break;
      case '4': // View previous versions (disabled)
        message.info('This feature is not available yet.');
        break;
      case '5': // Add Tag
        openAddTagModal(docId);
        break;
      case '6': // Map Data
        navigate(`/probate-docs/${docId}/map-fields`);
        break;
      case '8': // Delete Document
        confirmDeleteDocument(docId);
        break;
      default:
        break;
    }
  };

  /**
   * Runs one download, keeping the user informed while it happens.
   *
   * Generating a filled document is slow: the server loads the estate, works
   * out the family tree and fills every mapped box before a single byte comes
   * back. Nothing said so, so the menu item simply did nothing for several
   * seconds, and people either clicked it again, queueing a second identical
   * job, or navigated away and lost the one already running.
   *
   * Reports the two waits separately, because they fail differently and a
   * stuck one tells you which end is stuck: "Preparing" is the server building
   * the document, "Downloading" is it arriving.
   */
  const runDownload = async (
    docId: string,
    what: string,
    path: string,
    fallbackName: string,
  ) => {
    // A second click while the first is still running would queue another
    // build of the same document rather than hurry the one in flight.
    if (downloadingDoc) return;

    const key = `download-${docId}`;
    setDownloadingDoc(docId);
    message.open({ key, type: 'loading', content: `Preparing ${what}...`, duration: 0 });

    try {
      const response = await api.get(path, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          // Only fires once the server starts sending, which is exactly the
          // moment the wait stops being a build and starts being a transfer.
          const total = event.total;
          const done = total ? Math.round((event.loaded / total) * 100) : null;
          message.open({
            key,
            type: 'loading',
            content: done === null
              ? `Downloading ${what}...`
              : `Downloading ${what}... ${done}%`,
            duration: 0,
          });
        },
      });

      saveBlob(response, fallbackName);
      message.open({ key, type: 'success', content: `${what} downloaded.`, duration: 2 });
    } catch (error: any) {
      console.error(error);
      let errorMessage = `Could not download ${what}.`;
      const errorData = error.response?.data;
      if (errorData instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorData.text());
          if (parsed?.message) errorMessage = parsed.message;
        } catch {
          // Non-JSON responses retain the download fallback message.
        }
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      message.open({
        key,
        type: 'error',
        content: errorMessage,
        duration: 4,
      });
    } finally {
      setDownloadingDoc(null);
    }
  };

  /**
   * Hands the response to the browser as a file, named as the server named it.
   */
  const saveBlob = (response: any, fallbackName: string) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const disposition = response.headers['content-disposition'];
    let filename = fallbackName;
    if (disposition) {
      const match = disposition.match(/filename="([^"]+)"|filename=([^;\s]+)/i);
      if (match) filename = (match[1] || match[2]).replace(/^"|"$/g, '').trim();
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /** Downloads the original system version of the probate document. */
  const handleDownloadOriginal = (docId: string) =>
    runDownload(docId, 'the original document', `/probatedocs/${docId}/download/original`,
      `original_${docId}.pdf`);

  /** Downloads the autogenerated filled version. */
  const handleDownloadFilled = (docId: string) => {
    if (!selectedEstate) {
      message.error('Please select an estate first.');
      return;
    }
    return runDownload(docId, 'the filled document',
      `/probatedocs/${docId}/download/filled/${selectedEstate.uuid}`, `filled_${docId}.pdf`);
  };

  /** Downloads the user's own uploaded version. */
  const handleDownloadUserVersion = (docId: string) =>
    runDownload(docId, 'your updated document', `/probatedocs/${docId}/download/user`,
      `user_updated_${docId}.pdf`);

  /**
   * Opens the upload modal for the selected probate document.
   * @param docId - The UID of the probate document.
   */
  const openUploadModal = (docId: string) => {
    setCurrentDocId(docId);
    setFileList([]);
    setUploadModalVisible(true);
  };

  /**
   * Handles the upload action when the user confirms the upload.
   */
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select a file to upload.');
      return;
    }

    setUploading(true);
    const file = fileList[0];

    const formData = new FormData();
    formData.append('file', file as RcFile);

    try {
      const response = await api.post(`/probatedocs/${currentDocId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('File uploaded successfully.');
      setUploadModalVisible(false);

      // Refresh the documents list to reflect changes
      refreshDocuments();
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || 'Failed to upload file.');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Closes the upload modal without uploading.
   */
  const handleCancelUpload = () => {
    setUploadModalVisible(false);
  };

  /**
   * Handles file selection in the Upload component.
   * @param param0 - The upload change parameters.
   */
  const onChangeUpload = ({ fileList }: UploadChangeParam<UploadFile>) => {
    setFileList(fileList.slice(-1)); // Only keep the latest file
  };

  /**
   * Opens the add tag modal for the selected probate document.
   * @param docId - The UID of the probate document.
   */
  const openAddTagModal = (docId: string) => {
    setSelectedDocId(docId);
    setTagName('');
    setAddTagModalVisible(true);
  };

  /**
   * Handles adding a new tag to the selected probate document.
   */
  const handleAddTag = async () => {
    if (!tagName.trim()) {
      message.error('Tag name cannot be empty.');
      return;
    }

    try {
      const response = await api.post(`/probatedocs/${selectedDocId}/tags`, { name: tagName.trim() });
      message.success('Tag added successfully.');
      setAddTagModalVisible(false);

      // Refresh the documents list to reflect changes
      refreshDocuments();
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || 'Failed to add tag.');
    }
  };

  /**
   * Closes the add tag modal without adding a tag.
   */
  const handleCancelAddTag = () => {
    setAddTagModalVisible(false);
  };

  /**
   * Removes a tag from the selected probate document.
   * @param probateDocId - The UID of the probate document.
   * @param tagId - The ID of the tag to remove.
   */
  const handleRemoveTag = async (probateDocId: string, tagId: number) => {
    try {
      const response = await api.delete(`/probatedocs/${probateDocId}/tags/${tagId}`);
      message.success('Tag removed successfully.');

      // Refresh the documents list to reflect changes
      refreshDocuments();
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || 'Failed to remove tag.');
    }
  };

  /**
   * Asks before deleting, because the document and every version uploaded
   * against it go with it.
   * @param probateDocId - The UID of the probate document.
   */
  const confirmDeleteDocument = (probateDocId: string) => {
    const doc = documents.find((d: any) => d.uid === probateDocId);
    Modal.confirm({
      title: 'Delete this document?',
      content: doc
        ? `"${doc.title}" and any versions uploaded against it will be removed.`
        : 'The document and any versions uploaded against it will be removed.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => handleDeleteDocument(probateDocId),
    });
  };

  /**
   * Deletes a probate document.
   * @param probateDocId - The UID of the probate document.
   */
  const handleDeleteDocument = async (probateDocId: string) => {
    try {
      await api.delete(`/probatedocs/${probateDocId}`);
      message.success('Document deleted successfully.');
      refreshDocuments();
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || 'Failed to delete document.');
    }
  };

  /**
   * Refreshes the documents list by fetching from the backend.
   */
  const refreshDocuments = async () => {
    setLoadingDocs(true);
    setErrorDocs(null);
    try {
      const response = await api.get('/probatedocs');
      setDocuments(response.data);
    } catch (error: any) {
      console.error(error);
      setErrorDocs(error.response?.data?.message || 'Failed to fetch documents.');
    } finally {
      setLoadingDocs(false);
    }
  };

  /**
   * Generates the menu for each probate document.
   * @param docId - The UID of the probate document.
   * @returns The menu component.
   */
  const menu = (docId: string) => (
    <Menu onClick={(e) => handleMenuClick(e, docId)}>
      <Menu.Item
        key="1"
        icon={downloadingDoc === docId ? <LoadingOutlined /> : <DownloadOutlined />}
        disabled={!!downloadingDoc}
      >
        Download Original (Unfilled)
      </Menu.Item>
      <Menu.Item
        key="preview"
        icon={<EyeOutlined />}
      >
        Preview Filled Document
      </Menu.Item>
      <Menu.Item
        key="7"
        icon={downloadingDoc === docId ? <LoadingOutlined /> : <DownloadOutlined />}
        disabled={!!downloadingDoc}
      >
        Download Filled (Autogenerated)
      </Menu.Item>
      <Menu.Item
        key="2"
        icon={downloadingDoc === docId ? <LoadingOutlined /> : <DownloadOutlined />}
        disabled={!!downloadingDoc}
      >
        Download User Updated
      </Menu.Item>
      <Menu.Item disabled={!canManage} key="3" icon={<UploadOutlined />}>
        Upload User Updated Document
      </Menu.Item>
      <Menu.Item key="4" icon={<EyeOutlined />} disabled>
        View Previous Versions
      </Menu.Item>
      <Menu.Item disabled={!canManage} key="5" icon={<TagOutlined />}>
        Add Tag
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item disabled={!canManage} key="6" icon={<PartitionOutlined />}>
        Map Data Fields
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item disabled={!canManage} key="8" icon={<DeleteOutlined />} danger>
        Delete Document
      </Menu.Item>
    </Menu>
  );

  // Handle loading and error states for documents
  if (loadingDocs) {
    return (
      <DocumentContainer>
        <Spin tip="Loading documents..." />
      </DocumentContainer>
    );
  }

  if (errorDocs) {
    return (
      <DocumentContainer>
        <Alert message="Error" description={errorDocs} type="error" showIcon />
      </DocumentContainer>
    );
  }

  return (
    <DocumentContainer>
      <Title level={3}>Probate Documents</Title>
      <Divider />

      <List
        dataSource={documents}
        renderItem={(doc) => (
          <DocumentItem key={doc.id}>
            <DocumentDetails>
              <DocumentTitle level={4}>{doc.title}</DocumentTitle>
              <DocumentDescription>
                <Tooltip title={doc.description}>
                  {doc.description}
                </Tooltip>
              </DocumentDescription>
              <DocumentTags>
                {doc.tags.map((tag: Tag) => (
                  <Tag
                    key={tag.id}
                    closable={canManage}
                    onClose={() => handleRemoveTag(doc.uid, tag.id)}
                  >
                    {tag.name}
                  </Tag>
                ))}
                <Tag
                  onClick={() => canManage && openAddTagModal(doc.uid)}
                  style={{ background: '#fff', borderStyle: 'dashed', cursor: 'pointer' }}
                >
                  + Add Tag
                </Tag>
              </DocumentTags>
            </DocumentDetails>

            <Space size="large" style={{ textAlign: 'right' }}>
              <DocumentStatus status={doc.status}>{doc.status}</DocumentStatus>
              <Dropdown overlay={menu(doc.uid)}>
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            </Space>
          </DocumentItem>
        )}
      />

      {/* Upload Modal */}
      <Modal
        title="Upload Updated Document"
        visible={uploadModalVisible}
        onOk={handleUpload}
        onCancel={handleCancelUpload}
        confirmLoading={uploading}
        okText="Upload"
      >
        <Upload
          beforeUpload={() => false} // Prevent automatic upload
          onChange={onChangeUpload}
          fileList={fileList}
          maxCount={1}
          accept=".pdf,.doc,.docx"
        >
          <Button icon={<UploadOutlined />}>Select File</Button>
        </Upload>
        <p style={{ marginTop: '1rem' }}>Allowed file types: PDF, DOC, DOCX. Max size: 10MB.</p>
      </Modal>

      {/* Add Tag Modal */}
      <Modal
        title="Add Tag to Document"
        visible={addTagModalVisible}
        onOk={handleAddTag}
        onCancel={handleCancelAddTag}
        okText="Add Tag"
        cancelText="Cancel"
      >
        <Input
          placeholder="Enter tag name"
          value={tagName}
          onChange={(e) => setTagName(e.target.value)}
          onPressEnter={handleAddTag}
        />
      </Modal>

      {/* Floating Button */}
      <FloatingButton
        hidden={!canManage}
        type="primary"
        shape="circle"
        icon={<PlusOutlined />}
        onClick={() => setIsAddModalVisible(true)}
      />

      {/* Add Probate Document Modal */}
      <AddProbateDocumentModal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onSuccess={refreshDocuments}
      />

      {previewDocId && (
        <PdfPreviewModal
          visible={previewVisible}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewDocId('');
            setPreviewDocTitle('');
          }}
          probateDocId={previewDocId}
          docTitle={previewDocTitle}
        />
      )}
    </DocumentContainer>
  );
};

export default DocumentsPage;
